/* eslint-disable camelcase */
// @ts-nocheck
import React from 'react';
import { difference, sortBy } from 'lodash';
import cx from 'classnames';
import PermissionsEditor from '../shared/permissions-editor';
import { Member } from '../models/member';
import { Role } from '../models/role';
import { Item } from '../shared/select-two-column';
import css from '../shared/css';
import { MemberIcon } from '../icons';
import { AppContext } from '../hooks/use-app';

interface Props {
  roles: Role[];
  form_id: string;
  isMenuItem?: boolean;
}

interface State {
  roles: Item[];
  loading: boolean;
  showEditor: boolean;
  memberships: MembershipItem[];
  selectedMembers: Member[];
  showSuccessAlert: boolean;
  errors: string[];
  loadingLabel: string;
}

export interface MembershipItem {
  id: string;
  name: string;
  email?: string;
  is_disabled?: boolean;
  role_name?: string;
  user?: string;
}

export default class PermissionMembers extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      roles: [],
      errors: [],
      loading: true,
      showEditor: false,
      memberships: [],
      selectedMembers: [],
      showSuccessAlert: false,
      loadingLabel: 'Loading...',
    } as State;
  }

  handleShowEditor = () => {
    this.setState({ showEditor: true });
    return this.setPermissionMembers();
  };

  handlePermissionsDone(permissions: string[], touched) {
    const { selectedMembers, errors } = this.state;
    const { form_id } = this.props;
    const previousMemberIds = selectedMembers.map((member) => member.id);

    if (!touched) {
      this.setState({ showEditor: false });
      this.setState({ loadingLabel: 'Loading...' });
    } else {
      this.setState({ loading: true });
      this.setState({ loadingLabel: 'Processing...' });

      const type = 'form_members';
      const add = difference(permissions, previousMemberIds);
      const remove = difference(previousMemberIds, permissions);
      const changes = {
        type,
        form_id,
        add,
        remove,
      };

      const { context } = this;
      context.api.forms
        .changePermissions(changes)
        .then(async () => {
          this.setState({ showSuccessAlert: true });
          try {
            await this.setPermissionMembers();
          } catch (error) {
            console.error(
              'Error while calling setting member permission:',
              error,
            );
          }

          setTimeout(() => {
            this.setState({
              showSuccessAlert: false,
              showEditor: false,
              loading: false,
            });
          }, 2000);
        })
        .catch((error) => {
          console.error('Change permissions error: ', error);
          this.setState({
            loading: false,
            errors: [...errors, 'Error while changing member permissions'],
          });
        });
    }
  }

  async setPermissionMembers() {
    const allMembers = await this.fetchMemberships();
    const formMembers = await this.fetchFormMembers();
    const memberships: MembershipItem[] = this.formatMembers(
      allMembers,
      formMembers,
    );
    const formMemberIds = formMembers.map((member) => member.membership_id);
    const filteredFormMembers = allMembers.filter((member) => formMemberIds.includes(member.id));

    const formattedRoles = this.formatRoles();
    formattedRoles.unshift({ label: 'All Roles', value: '', is_owner: false });

    this.setState({
      memberships,
      selectedMembers: filteredFormMembers,
      roles: formattedRoles,
      loading: false,
    });
  }

  get ownerRoles(): string[] {
    const { roles } = this.props;
    return roles
      .filter((role) => role.can_manage_roles)
      .map((role) => role.name);
  }

  async fetchMemberships(): Promise<Member[]> {
    const { api } = this.context;
    const { objects } = await api.memberships.all({ per_page: 5000 });
    return sortBy(objects, 'first_name');
  }

  async fetchFormMembers(): Promise<Member[]> {
    const { api } = this.context;
    const { form_id } = this.props;
    const { objects } = await api.permissions.all({
      object_id: form_id,
      type: 'form_members',
    });
    return sortBy(objects, 'first_name');
  }

  formatRoles(): Item[] {
    const { roles } = this.props;
    return roles.map((role) => ({
      label: role.name,
      value: role.id,
      is_owner: role.can_manage_roles,
    }));
  }

  formatMembers(members: Member[], formMembers): MembershipItem[] {
    return members.map((member) => {
      const formMembership = formMembers.find(
        (item) => item.membership_id === member.id,
      );
      return {
        email: member.email,
        name: member.user,
        id: member.id,
        role_name: member.role_name,
        is_disabled:
          this.ownerRoles.indexOf(member.role_name) !== -1
          || formMembership?.from_group,
        from_group: formMembership?.from_group ?? false,
      };
    });
  }

  render() {
    const { isMenuItem } = this.props;
    const {
      showEditor, loading, roles, errors,
      showSuccessAlert, memberships, selectedMembers, loadingLabel,
    } = this.state;
    const className = isMenuItem ? styles.memberIconMenu : styles.memberIcon;

    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href="#"
          data-testid="member-permissions-button"
          className={
            isMenuItem
              ? styles.menuContainerItem
              : cx(styles.iconContainer, 'btn btn-quiet outlined small left')
          }
          onClick={this.handleShowEditor}
        >
          <MemberIcon
            data-testid="member-icon"
            className={className}
            width="15"
            color="#41403b"
          />
          {isMenuItem && (
            <span data-testid="menu-item" className={styles.memberMenuCopy}>
              Member Permissions
            </span>
          )}
        </a>
        <PermissionsEditor
          data-testId="permissions-editor"
          type="members"
          show={showEditor}
          errors={errors}
          roles={roles}
          loading={loading}
          loadingLabel={loadingLabel}
          successAlert={showSuccessAlert}
          objects={memberships}
          selectedIDs={selectedMembers.map((s) => s.id)}
          onDone={
            (changes: string[], touched: boolean) => this.handlePermissionsDone(changes, touched)
          }
          onCancel={() => this.setState({
            showEditor: false,
            loadingLabel: 'Loading...',
          })}
        />
      </>
    );
  }
}

PermissionMembers.contextType = AppContext;

const styles = css({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: 18,
  },
  iconContainer: {
    color: '#41403b',
    display: 'flex',
    marginLeft: 2,
    borderColor: '#fff',

    ':hover': {
      background: '#f4f4f4',
      color: '#41403b',
      borderColor: '#eee',
    },
  },
  memberIcon: {
    position: 'relative',
    top: 3,
    right: 1,
  },
  memberIconMenu: {
    marginRight: '0px',
  },
  menuContainerItem: {
    display: 'flex!important',
    marginLeft: '2px!important',
    borderColor: '#fff!important',
    padding: '3px 20px!important',
    fontWeight: 'normal!important',
    lineHeight: '20px!important',
    color: '#333333!important',
    whiteSpace: 'nowrap!important',
  },
  memberMenuCopy: {
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  },
  counter: {
    textAlign: 'center',
    fontSize: 12,
  },
});
